import "./applications.scss";
import type * as React from "react";
import { type FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { showDialog } from "../../Actions/DialogActions";
import Button from "../../Common/Button";
import HoverError from "../../Utilities/HoverError";
import { useTitle } from "../../Utilities/TitleHook";
import ApplicationCreate from "./ApplicationCreate";
import ApplicationLine, { type ApplicationType } from "./ApplicationLine";

const Applications: FC = () => {
    useTitle("Applications");

    const dispatch = useDispatch();
    const [ applications, setApplications ] = useState<Array<ApplicationType>>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState(false);
    const [ createLoading, setCreateLoading ] = useState(false);
    const [ createError, setCreateError ] = useState(false);

    useEffect(() => {
        if(createLoading) {
            return;
        }

        setLoading(true);

        fetch("/applications", {
            credentials: "same-origin",
            headers    : new Headers({
                "Accept": "application/json",
            }),
        })
            .then((response) => {
                setLoading(false);

                if(response.status !== 200) {
                    throw new Error("Invalid response from server");
                }

                return response.json();
            })
            .then((result) => {
                setApplications(result);
            })
            .catch((err) => {
                console.error(err);
                setError(err.toString());
            });
    }, [ createLoading ]);

    let before: React.ReactNode = "";

    if(createError) {
        before = <div>An error occurred while creating the application</div>;
    }

    if(error) {
        before = <HoverError>{error}</HoverError>;
    }

    let applicationText = <p>We are loading your applications...</p>;

    if(!loading) {
        if(!applications.length) {
            applicationText = <p>Looks like you do not have any applications :(</p>;
        } else {
            applicationText = (
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Redirect URI</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {applications.map((application, index) => (
                            <ApplicationLine
                                key={application.client_id}
                                index={index}
                                application={application}
                            />
                        ))}
                    </tbody>
                </table>
            );
        }
    }

    return (
        <main id="applications">
            {before}
            <h1 className="text-4xl">
                Your Applications
                <Button
                    onClick={() => {
                        dispatch(showDialog(
                            "Create New Application",
                            <ApplicationCreate
                                onSubmit={(name: string, redirectUri: string) => {
                                    setCreateLoading(true);

                                    fetch("/application", {
                                        method     : "POST",
                                        credentials: "same-origin",
                                        headers    : new Headers({
                                            "Accept"      : "application/json",
                                            "Content-Type": "application/json",
                                        }),
                                        body: JSON.stringify({
                                            name    : name,
                                            redirect: redirectUri,
                                        }),
                                    })
                                        .then(response => {
                                            setCreateLoading(false);

                                            if(response.status !== 200) {
                                                console.error("API Documentation: Invalid Response", response);
                                                throw new Error("Invalid response");
                                            }

                                            return response.json();
                                        })
                                        .then(() => {
                                            setCreateLoading(false);
                                            setCreateError(false);
                                        })
                                        .catch((err) => {
                                            setError(err.toString());
                                        });
                                }}
                            />
                        ));
                    }}
                    classes={[ "create-new" ]}
                >
                    <FontAwesomeLight icon="plus" /> New
                </Button>
            </h1>
            <div className="mt-4">
                {applicationText}
            </div>
        </main>
    );
};

export default Applications;
